/**
 * The names of the video path that the chat needs without converting
 * anything (1.187.0, review 2026-10b D1): which encoders, which level, which
 * data rate, the words for a tile, whether this device converts at all.
 *
 * **Why apart from `videowandeln.ts`.** Six places of the main bundle took
 * one of these names from there, and with them came the whole conversion
 * (the HDR tone mapper, the MP4 readers, the stamp) into every start of the
 * chat, some 50 KB nobody needs before choosing a video. The conversion is
 * now loaded on its first use (`lib/video.ts`), and these stay at hand.
 */
import { groesse } from './format'
import { WORTLAUT } from '../texte'

/**
 * Wohin kodiert wird, in dieser Reihenfolge.
 *
 * H.264 und nichts anderes: der Sinn der Übung ist, dass das Video **überall**
 * läuft. `4d0028` ist Main/Level 4 (bis 1080p), `42001f` Baseline als
 * Rückfall für ältere Geräte. The level in these names is the lowest one
 * asked for; `withLevel()` raises it to what the frame needs.
 */
export const ZIELKODIERUNGEN = ['avc1.4d0028', 'avc1.42001f'] as const

/**
 * Where an HDR recording goes when it stays HDR (1.179.0, #155,
 * `Wandelziel.hdr === 'keep'`): HEVC Main10, Main tier, level 4 as the
 * least -- the one HDR codec a browser can write at all (Safari, and an
 * Android with the hardware). H.264 has no ten bits in any browser, and
 * VP9 or AV1 with ten bits plays on no iPhone. The level is raised by
 * `withLevel()` like the H.264 one. Where none of this is there, the
 * recording goes the SDR way with a note.
 */
export const HDR_ZIELKODIERUNGEN = ['hvc1.2.4.L120.B0'] as const

/**
 * HEVC levels, Main tier (ITU-T H.265, Tables A.8 and A.9): luma samples
 * per picture, per second, and kbit/s. The number is the `L` in the name,
 * thirty times the level.
 */
const HEVC_LEVELS: readonly { idc: number; frame: number; second: number; kbit: number }[] = [
  { idc: 120, frame: 2_228_224, second: 66_846_720, kbit: 12000 },
  { idc: 123, frame: 2_228_224, second: 133_693_440, kbit: 20000 },
  { idc: 150, frame: 8_912_896, second: 267_386_880, kbit: 25000 },
  { idc: 153, frame: 8_912_896, second: 534_773_760, kbit: 40000 },
  { idc: 156, frame: 8_912_896, second: 1_069_547_520, kbit: 60000 },
  { idc: 180, frame: 35_651_584, second: 1_069_547_520, kbit: 60000 },
  { idc: 183, frame: 35_651_584, second: 2_139_095_040, kbit: 120000 },
]

/**
 * H.264 levels with their limits (ITU-T H.264, Table A-1): macroblocks per
 * frame, macroblocks per second, and the bitrate in kbit/s for Baseline and
 * Main. The byte is the last two hex digits of an `avc1` name.
 */
const AVC_LEVELS: readonly { byte: number; frame: number; second: number; kbit: number }[] = [
  { byte: 0x1f, frame: 3600, second: 108000, kbit: 14000 },
  { byte: 0x20, frame: 5120, second: 216000, kbit: 20000 },
  { byte: 0x28, frame: 8192, second: 245760, kbit: 20000 },
  { byte: 0x29, frame: 8192, second: 245760, kbit: 50000 },
  { byte: 0x2a, frame: 8704, second: 522240, kbit: 50000 },
  { byte: 0x32, frame: 22080, second: 589824, kbit: 135000 },
  { byte: 0x33, frame: 36864, second: 983040, kbit: 240000 },
  { byte: 0x34, frame: 36864, second: 2073600, kbit: 240000 },
]

/**
 * The `avc1` name with a level that carries this frame (1.157.1).
 *
 * From the field: a screen recording from the iPad (2752 x 2064, 4:3)
 * failed on the iPhone with "Encoding task failed", and the diagnosis said
 * `avc1.4d0028 1920x1440`. Level 4 takes 8192 macroblocks per frame, and
 * 1920 x 1440 has 10800: every 4:3 or 3:4 video at full size broke, and
 * so would every 4K film. `isConfigSupported()` said yes to it; the encoder
 * failed only at the first frame. 1920 x 1080 has 8160 and fitted by 32.
 *
 * The level only ever goes up from the one in the name, never down: the
 * name says the least a caller wants. A name that is not `avc1`/`avc3`, or
 * a frame no level carries, comes back unchanged.
 */
export function withLevel(codec: string, width: number, height: number, fps: number, bitrate?: number): string {
  // HEVC (1.179.0): `hvc1.2.4.L120.B0`, the level in luma samples, not macroblocks.
  const hevc = /^(hvc1|hev1)(\.\d+\.[0-9a-f]+\.)L(\d+)(\..*)?$/i.exec(codec)
  if (hevc !== null) {
    const base = parseInt(hevc[3] ?? '0', 10)
    const frame = width * height
    const second = frame * Math.max(1, fps)
    const kbit = bitrate === undefined ? 0 : bitrate / 1000
    const level = HEVC_LEVELS.find((l) => l.idc >= base && l.frame >= frame && l.second >= second && l.kbit >= kbit)
    if (level === undefined) return codec
    return `${hevc[1]}${hevc[2]}L${level.idc}${hevc[4] ?? ''}`
  }
  const match = /^(avc[13]\.[0-9a-f]{4})([0-9a-f]{2})$/i.exec(codec)
  if (match === null) return codec
  const prefix = match[1] ?? ''
  const base = parseInt(match[2] ?? '0', 16)
  const frame = Math.ceil(width / 16) * Math.ceil(height / 16)
  const second = frame * Math.max(1, fps)
  const kbit = bitrate === undefined ? 0 : bitrate / 1000
  const level = AVC_LEVELS.find((l) => l.byte >= base && l.frame >= frame && l.second >= second && l.kbit >= kbit)
  if (level === undefined) return codec
  return prefix + level.byte.toString(16).padStart(2, '0')
}

export interface Wandelziel {
  /** Längste Kante in Bildpunkten — aus den Einstellungen (`video_max_edge`). */
  maxEdge: number
  /** Höchste Bildrate — aus den Einstellungen (`video_max_fps`). */
  maxFps: number
  /**
   * Womit kodiert werden darf.
   *
   * **Diese Tür gibt es für den Prüflauf**, und das gehört gesagt: das
   * Chromium in der Prüfumgebung kann H.264 überhaupt nicht — weder kodieren
   * noch dekodieren. Ohne die Möglichkeit, eine andere Kodierung vorzugeben,
   * ließe sich die ganze Mechanik (Zerlegen, Zeichnen, Auslassen, Drehen,
   * Schreiben) nirgends messen. Im Betrieb wird sie nicht gesetzt, und dann
   * gilt `ZIELKODIERUNGEN`.
   */
  kodierungen?: readonly string[]
  /**
   * Welchen Weg das Bild zum Kodierer nimmt — `direkt` oder über die
   * `leinwand`.
   *
   * **Diese Tür gibt es für den Prüflauf**, wie `kodierungen`, und aus einem
   * handfesten Grund: normalerweise wird die Frage gestellt
   * (`skaliertSelbst()`), und auf jedem Gerät fällt die Antwort gleich aus.
   * Damit liefe der jeweils andere Zweig **nirgends** — und das ist der
   * Zweig, der einspringt, wenn ein Kodierer eben *nicht* selbst skaliert.
   * Ein Rückfall, den nichts je durchläuft, ist keiner.
   *
   * Im Betrieb wird sie nicht gesetzt, und dann entscheidet die Messung.
   */
  skalieren?: 'direkt' | 'leinwand'
  /**
   * Wie der Kodierer rechnen soll — `quality` oder `realtime`.
   *
   * Aus den Einstellungen (`video_latency`). **Am iPhone gemessen bringt
   * `realtime` keine Zeit und kostet die halbe Datenrate** — derselbe Film in
   * 29,9 s statt 29,8 s, aber 37 MB statt 73 MB. Die Begründung steht bei der
   * Einstellung; hier zählt nur, dass die Angabe ankommt.
   *
   * Fehlt sie, gilt `quality` — genau das, was hier vorher fest stand.
   */
  latenz?: 'quality' | 'realtime'
  /**
   * The step of the data rate, from the settings (`video_bitrate`, 1.166.0).
   * Missing, it is `standard` -- what stood here before.
   */
  datenrate?: Datenratenstufe
  /**
   * What becomes of an HDR source (1.179.0, #155). Unset, it is turned into
   * SDR (`lib/hdrToSdr.ts`, 1.178.0). `keep` writes HDR where this device
   * has an encoder for it (`HDR_ZIELKODIERUNGEN`) that scales by itself --
   * the author's switch `hdr_video`; without one the recording goes the SDR
   * way again, and `hdrNote` at the result says why. `raw` is a door for
   * the bench: the frame goes into the encoder as it comes, the way before
   * 1.178.0 (the counter-probe of `hdr.mjs`).
   */
  hdr?: 'keep' | 'raw'
  /**
   * The encoders allowed for an HDR result -- a door for the bench like
   * `kodierungen`: this Chromium has no HEVC, VP9 with ten bits stands in.
   */
  hdrKodierungen?: readonly string[]
  /**
   * An SDR file beside an HDR one (`hdr_video_fallback`). Read by
   * `uploadVideo()`, which converts a second time; the conversion itself
   * does not look at it.
   */
  hdrFallback?: boolean
}

export interface Wandelergebnis {
  /** Was hochgeladen wird — verkleinert oder das Original. */
  datei: File
  verkleinert: boolean
  /** Warum nicht verkleinert wurde. Leer, wenn es geklappt hat. */
  grund: string
  vorher: number
  nachher: number
  /**
   * Die Maße des Ergebnisses — `0`, wenn nichts gerechnet wurde.
   *
   * Damit die Oberfläche sagen kann, **was** herausgekommen ist. „Es wurde
   * verkleinert" ohne Zahl ist eine Behauptung; wer sie nachprüfen will,
   * müsste die Datei suchen.
   */
  breite: number
  hoehe: number
  /**
   * Wie lange das Video läuft, in Millisekunden — `0`, wenn unbekannt.
   *
   * **Steht hier, damit niemand ein `<video>`-Element danach fragen muss.**
   * Genau das hat den Upload zum Hängen gebracht (#4): das Element wartete
   * nach dem letzten Stück auf ein `loadedmetadata`, das auf manchen Geräten
   * nie kam. Der Zerleger weiß es ohnehin — aus `duration` und `timescale`
   * der Spur.
   */
  dauerMs: number
  /**
   * Die Kodierung der Quelle und die des Ergebnisses.
   *
   * **Für die Kachel** (#4, „bei Videos … auch noch das Format darstellen"),
   * und sie beantwortet nebenbei die Frage, warum dort 360p steht: ist
   * `quellkodierung` schon H.264 und wurde nichts gerechnet, dann war die
   * Aufnahme selbst so klein. Leer heißt: nicht gelesen.
   */
  quellkodierung: string
  zielkodierung: string
  /**
   * Das Standbild, beim Umrechnen abgefallen — `null`, wenn es keins gibt.
   *
   * **Der kurze Weg zum Vorschaubild** (#4). Wurde gerechnet, lag jedes Bild
   * ohnehin auf der Leinwand; eines davon liegt hier. Wurde nichts gerechnet
   * (`schon-gut`), steht hier `null`, und `uploadVideo()` geht den alten Weg
   * über ein `<video>`-Element — das sind kleine Dateien, dort hat es nie
   * geklemmt.
   */
  standbild: Blob | null
  /**
   * Läuft die Datei, die hier herauskommt, **überall** (#41)?
   *
   * Aus dem Betrieb: „egal welches Ausgangsformat das Bild/Video hat (HEVC,
   * HEIC, …) → es landet IMMER in einem kompatiblen Format (iOS, Windows,
   * Android)."
   *
   * `true` heißt: frisch nach H.264 gerechnet, oder die Quelle war es schon.
   * `false` heißt: hier liegt das Original, und es ist keins von beiden --
   * dann darf es **nicht** hochgeladen werden, und `grund` sagt warum. Die
   * Entscheidung darüber trifft `uploadVideo()`; hier steht nur der Befund.
   */
  kompatibel: boolean
  /**
   * Whether the file is HDR (1.179.0, #155): written as HEVC Main10 with
   * the source's HLG or PQ, or an HDR original that stayed as it was. The
   * server reads the same from the file; this is for the tile and for the
   * second conversion of `uploadVideo()`.
   */
  hdr: boolean
  /**
   * Why an HDR recording went out as SDR although `hdr: 'keep'` asked for
   * HDR -- no encoder, or one that does not scale by itself. Empty when it
   * is HDR or nobody asked. A sentence for the tile, like `grund`.
   */
  hdrNote: string
}

/**
 * Ist auf dieser Fläche nichts als Schwarz? Stichprobe über neun Punkte.
 *
 * **Hier, weil es zwei Stellen braucht** (#4): das Standbild aus dem
 * Umrechnen prüft eine `OffscreenCanvas`, `standbild()` in `video.ts` eine
 * gewöhnliche. Zweimal dieselbe Stichprobe wäre genau das, was auseinander
 * läuft — und `video.ts` holt sich ohnehin schon von hier, umgekehrt wäre es
 * ein Kreis.
 *
 * **Geprüft wird auf schwarz, nicht auf „überall dieselbe Farbe":** ein Bild
 * darf einfarbig sein. Eine weiße Wand, Nebel, ein Nachthimmel — alles
 * gültige Standbilder, und sie wegzuwerfen wäre schlimmer als der Fehler, den
 * diese Prüfung sucht.
 */
export function schwarz(
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  breite: number,
  hoehe: number,
): boolean {
  const punkte = [0.2, 0.5, 0.8]

  for (const x of punkte) {
    for (const y of punkte) {
      const [r, g, b] = context.getImageData(
        Math.floor(breite * x),
        Math.floor(hoehe * y),
        1,
        1,
      ).data
      // Nicht auf exakt 0 prüfen: ein dunkles Bild aus der Dämmerung liegt
      // knapp darüber, eine leere Leinwand nicht.
      if ((r ?? 0) + (g ?? 0) + (b ?? 0) > 24) return false
    }
  }
  return true
}

/**
 * Die kurze Kante — das ist die Zahl, die „1080p" meint.
 *
 * Eine Hochkantaufnahme misst 1080 x 1920; „1920p" stünde dort und wäre
 * falsch. Gemeint ist immer die kleinere der beiden Kanten.
 */
export function kurzeKante(masse: { breite: number; hoehe: number }): number {
  if (masse.breite <= 0 || masse.hoehe <= 0) return 0
  return Math.min(masse.breite, masse.hoehe)
}

/**
 * Ein Kürzel, wie ein Mensch es liest: `avc1.4d0028` → `H.264`.
 *
 * Vier Zeichen aus einem Boxbaum sagen dem Betrachter nichts. Bleibt es
 * unbekannt, steht das Kürzel selbst da — geraten wird nicht.
 */
export function formatName(kodierung: string): string {
  switch (familie(kodierung)) {
    case 'avc': return 'H.264'
    case 'hevc': return 'HEVC'
    case 'vp9': return 'VP9'
    case 'av1': return 'AV1'
    default: return kodierung === '' ? '' : kodierung
  }
}

/**
 * Was aus dem Video wurde, als Satz: „33 MB → 2,4 MB · 1080p · HEVC → H.264".
 *
 * **An einer Stelle**, weil er an zweien steht — in der Eingabezeile des Chats
 * und im Formular am Highlight. Zwei Kopien desselben Satzes laufen
 * auseinander, sobald eine von beiden etwas dazubekommt.
 *
 * **Auch wenn nichts gerechnet wurde, steht hier etwas** (#4). Vorher gab es
 * an dieser Stelle ein `return null`, und genau deshalb sah man einer
 * unveränderten Aufnahme nichts an — die Meldung aus dem Betrieb lautete
 * „360p steht in der Kachel, die Einstellung ist 1080", und die Auflösung war
 * die einzige Zahl, die den Fall erklärt hätte. Sie war die der **Quelle**:
 * hochgerechnet wird nie.
 */
export function ausbeuteText(befund: Wandelergebnis): string | null {
  const kante = kurzeKante(befund)
  const von = formatName(befund.quellkodierung)
  const nach = formatName(befund.zielkodierung)

  const teile: string[] = []
  teile.push(
    befund.verkleinert
      ? `${groesse(befund.vorher)} → ${groesse(befund.nachher)}`
      : groesse(befund.nachher),
  )
  if (kante > 0) teile.push(`${kante}p`)
  // Zweimal dasselbe Format ist kein Pfeil wert.
  if (von !== '' && nach !== '' && von !== nach) teile.push(`${von} → ${nach}`)
  else if (nach !== '') teile.push(nach)
  else if (von !== '') teile.push(von)
  // What stayed HDR says so (1.179.0); the note for SDR stands as a hint.
  if (befund.hdr) teile.push(WORTLAUT.technik.video.hdrAusbeute)

  // Nur die Dateigröße wäre keine Auskunft -- die steht ohnehin schon da.
  return teile.length > 1 ? teile.join(' · ') : null
}

/** Kann dieser Browser überhaupt umrechnen? */
/**
 * Ein Paket nachladen -- **und sagen, was fehlt, wenn es nicht kommt.**
 *
 * `mp4box` und `mp4-muxer` werden erst beim ersten Video geholt (der Grund
 * steht oben). Kommt der Nachschlag nicht, wirft der Browser „Importing a
 * module script failed" -- und dieser Satz landete bis 1.45.0 in der Klammer
 * von `nichtUmgerechnetMitGrund()`, also in einem Satz, der dem **Gerät** die
 * Schuld gab. Aus dem Betrieb, mit Bildschirmfoto, und er stimmte nicht: das
 * Gerät konnte es, der Download war nicht da.
 *
 * Zwei Lagen, in denen das eintritt, und beide gehen vorbei: ohne Netz, und im
 * Augenblick eines FTP-Uploads, in dem die Datei gerade ersetzt wird -- eine
 * fehlende Datei beantwortet der Front-Controller mit der Hülle und Status
 * 200, und HTML ist kein Skript. Deshalb „versuch es noch einmal" und nicht
 * „dieses Gerät kann es nicht".
 *
 * **Die Absage aus #41 bleibt**: hochgeladen wird trotzdem nichts. Ein Video,
 * das die Hälfte nicht abspielen kann, ist der schlechtere Ausgang.
 */
export async function nachladen<T>(laden: () => Promise<T>): Promise<T> {
  try {
    return await laden()
  } catch {
    throw new Error(WORTLAUT.technik.video.nachschlagFehlt)
  }
}

export function kannWandeln(): boolean {
  return (
    typeof VideoEncoder !== 'undefined'
    && typeof VideoDecoder !== 'undefined'
    && typeof VideoFrame !== 'undefined'
    && typeof OffscreenCanvas !== 'undefined'
  )
}

/**
 * Whether a video is refused before it is touched: too large **and** this
 * device cannot make it smaller (1.101.0, review). A device that converts
 * brings any video under the limit, so the size alone says nothing there.
 * The chat and the point's form asked it this way; the map's "photos onto
 * the map" asked only the size and refused a video the chat took.
 */
export function videoTooLarge(file: File, maxMb: number | null | undefined): boolean {
  return !kannWandeln() && maxMb !== null && maxMb !== undefined && file.size > maxMb * 1024 * 1024
}

/**
 * Bits per pixel and frame for the three steps of `video_bitrate` (1.166.0).
 *
 * 0.08 is the rate that stood here alone, and it is the order H.264 needs
 * for moving scenes: 1080p at 30 frames lands at about 5 Mbit/s. From use,
 * with uploaded videos: "es sind immer noch Komprimierungsartefakte". 1080p
 * at 60 frames is 10, 19 or 31 Mbit/s, about 75, 140 or 233 MB a minute.
 * Steps and not a number in kbit/s: edge and frame rate anyone can judge,
 * a rate nobody can. The route film keeps `standard` (`routeFilm.ts`).
 */
export const DATENRATEN = { standard: 0.08, high: 0.15, very_high: 0.25 } as const

export type Datenratenstufe = keyof typeof DATENRATEN

/**
 * The data rate, computed from the frame and one of the steps above. One
 * place for the probe, the level (`withLevel()`) and `configure()`: above
 * 20 Mbit/s a 1080p video needs level 4.1, and a probe with another rate
 * than the encoder's said yes where the first frame failed (1.157.1).
 */
export function datenrate(breite: number, hoehe: number, fps: number, stufe: Datenratenstufe = 'standard'): number {
  return Math.round(breite * hoehe * Math.max(1, fps) * DATENRATEN[stufe])
}

/**
 * Aus `avc1.4d0028` wird `avc` — die Familie, die der Schreiber kennt.
 */
/**
 * Welche Kodierungsfamilie -- und **„unbekannt" ist eine eigene Antwort**.
 *
 * Hier stand `return 'avc'` als Rest, und das war eine stille Zusage: eine
 * Kodierung, die niemand kennt, galt damit als H.264 und wurde vom
 * Überspringen mitgenommen. Genau das darf nach #41 nicht sein -- was nicht
 * nachweislich H.264 ist, wird gerechnet.
 */
export function familie(codec: string): 'avc' | 'hevc' | 'vp9' | 'av1' | 'unbekannt' {
  if (codec.startsWith('avc1') || codec.startsWith('avc3')) return 'avc'
  if (codec.startsWith('hvc1') || codec.startsWith('hev1')) return 'hevc'
  if (codec.startsWith('vp09')) return 'vp9'
  if (codec.startsWith('av01')) return 'av1'
  return 'unbekannt'
}

/**
 * Was der Kodierer kann -- Kodierung **und** Rechenweg in einem Stück.
 *
 * **`prefer-hardware` ist keine Bitte, sondern eine Bedingung** -- und das
 * hat der Prüflauf gefunden, bevor es jemand am Gerät gemerkt hätte. Der
 * Name liest sich wie „nimm gern die Hardware, sonst eben nicht"; in Chromium
 * meldet `isConfigSupported()` damit `false`, wenn keine da ist, und
 * `configure()` bricht mit „Encoder creation error" ab. Auf einem Rechner
 * ohne H.264-Baustein -- viele Linux- und Windows-Kisten, manches Android --
 * wäre damit **jede** Umrechnung fehlgeschlagen, zweimal hintereinander, und
 * das Video danach gar nicht hinausgegangen (#41).
 *
 * Deshalb wird gefragt, **wie konfiguriert wird**: dieselbe Angabe in
 * derselben Form. Erst die Runde mit Hardware über alle Kandidaten, dann die
 * ohne -- die Beschleunigung wiegt schwerer als das Profil, beide Kandidaten
 * sind ohnehin H.264.
 */
export interface Kodiererwahl {
  codec: string
  hardware: 'prefer-hardware' | 'no-preference'
}

export async function ersteMoegliche(
  kandidaten: readonly string[],
  breite: number,
  hoehe: number,
  fps?: number,
  stufe: Datenratenstufe = 'standard',
): Promise<Kodiererwahl | null> {
  const wege: Kodiererwahl['hardware'][] = ['prefer-hardware', 'no-preference']
  for (const hardware of wege) {
    for (const named of kandidaten) {
      // Without a known rate the upload's ceiling (`VIDEO_MAX_FPS`) is the
      // safe guess: a level too high costs nothing, one too low the video.
      const codec = withLevel(named, breite, hoehe, fps ?? 30, fps === undefined ? undefined : datenrate(breite, hoehe, fps, stufe))
      try {
        // **Asked with the frame rate and the bitrate the film is encoded
        // with** (1.112.0, review), where the caller knows them: Level 4
        // takes 1920 x 1080 at 30 frames and not at 60, and a probe
        // without the rate said yes where `configure()` then failed --
        // with a different error than "kein Kodierer", so the still was
        // not taken. The rule above (ask with the same values) holds for
        // every value that is set.
        const befund = await VideoEncoder.isConfigSupported({
          codec,
          width: breite,
          height: hoehe,
          hardwareAcceleration: hardware,
          ...(fps === undefined ? {} : { framerate: fps, bitrate: datenrate(breite, hoehe, fps, stufe) }),
        })
        if (befund.supported === true) return { codec, hardware }
      } catch {
        // Ein unbekannter Name wirft statt `false` zu melden. Nächster.
      }
    }
  }
  return null
}
